/* eslint-disable no-magic-numbers */
import {
    Divider,
    FormControlLabel,
    Grid,
    Radio,
    RadioGroup,
    Typography,
    ImageList,
    ImageListItem,
    ImageListItemBar,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    IconButton,
    Snackbar,
    Alert,
    SnackbarCloseReason,
    Box,
    Stack,
} from "@mui/material"
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown"
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded'
import { useEffect, useState } from "react"
import moment from "moment"
import { LoadingButton } from "@mui/lab"
import { useTranslation } from "react-i18next"
import {
    FeeResponse,
    getMembershipRenewalFees,
} from "../../services/ntuc/memberService/payment"
import {
    ArrearsRoute,
    ArrearsRouteProps,
} from "../../views/arrears-payment-flow/ArrearsPaymentFlow/ArrearsPaymentFlow"
import { useCookies } from "react-cookie"
import { AppCookies } from "../../helpers/cookies"
import { hashString } from "../../helpers/StringHelpers"

export enum FeeType {
    NewApplication = 1,
    Renewal = 2,
    PayingGiroArrearsOrAdvances = 3,
}

type WarningPanelProps = {
    title: React.ReactNode; // first (red) line
    subtitle?: React.ReactNode; // second (neutral) line
    sx?: Record<string,string | number>; // optional style overrides
};

const WarningPanel = ({ title, subtitle, sx }: WarningPanelProps) => {
    return (
        <Box
            role="alert"
            aria-live="polite"
            sx={{
                bgcolor:  `rgba(219, 107, 107, 0.08)`, // soft red
                borderRadius: 2,
                px: 3,
                py: 2.5,
                boxShadow: 2,
                maxWidth: `36rem`,
                ...sx,
            }}
        >
            <Stack direction="row" spacing={2} alignItems="flex-start">
                <WarningAmberRoundedIcon
                    sx={{ color: `error.main`, fontSize: 28, mt: `2px`, flexShrink: 0 }}
                />

                <Box>
                    <Typography
                        sx={{
                            color: `error.main`,
                            fontWeight: 700,
                            letterSpacing: 0.2,
                            fontSize: { xs: 16, sm: 18 },
                            lineHeight: 1.4,
                        }}
                    >
                        {title}
                    </Typography>

                    {subtitle ? (
                        <Typography
                            sx={{
                                mt: 1.25,
                                color: `text.secondary`,
                                fontSize: { xs: 15, sm: 16 },
                                lineHeight: 1.6,
                            }}
                        >
                            {subtitle}
                        </Typography>
                    ) : null}
                </Box>
            </Stack>
        </Box>
    )
}

const MembershipFeePayment = (props: ArrearsRouteProps) => {
    const DECIMALS = 2
    const { onNavigateTo } = props
    const { t } = useTranslation(`arrears-payment`)
    const [loading, setLoading] = useState(false)
    const [fees, setFees] = useState<FeeResponse[]>([])
    const [selectedFee, setSelectedFee] = useState<FeeResponse | undefined>(
        undefined,
    )
    const [membershipId, setMembershipId] = useState<number | undefined>(
        undefined,
    )
    const [isArrear, setIsArrear] = useState<boolean>(false)
    const [periodExpanded, setPeriodExpanded] = useState<boolean[]>([])
    const [periodMessage, setPeriodMessage] = useState<string>(``)
    const [openPeriodAlert, setOpenPeriodAlert] = useState<boolean>(false)
    const [isShowErrorSpecialCampaign, setIsShowErrorSpecialCampaign] = useState<boolean>(false)
    const [cookies, setCookie] = useCookies()

    const handleConfigUpdate = async () => {
        const config = window.myNtucConfig || {}
        const storedHash = cookies[AppCookies.SESSION_HASH]

        const combined = `${config.membershipId || ``}${config.authToken || ``}`
        console.log(`combined string:`,combined)
        const currentHash = await hashString(combined)

        if (config.authToken && storedHash && currentHash !== storedHash) {
            setCookie(AppCookies.RENEWAL_CAMPAIGN_CODE, ``)
        }

        if (config.authToken && config.membershipId) {
            setCookie(AppCookies.SESSION_HASH, currentHash)
        }
    }

    const handleNextButton = () => {
        if (selectedFee) {
            if (isArrear) {
                onNavigateTo(ArrearsRoute.MembershipPaymentSummary, {
                    fee: selectedFee,
                })
            } else {
                onNavigateTo(ArrearsRoute.TermsAndConditions, {
                    fee: selectedFee,
                    membershipId,
                })
            }
        }
    }
    const handleClosePeriodAlert = (
        event?: React.SyntheticEvent | Event,
        reason?: SnackbarCloseReason,
    ) => {
        if (reason === `clickaway`) {
            return
        }

        setOpenPeriodAlert(false)
    }

    useEffect(() => {
        const fetchData = async () => {
            if (window.myNtucConfig.membershipId) {
                if (window.myNtucConfig.renewalCampaignCode !== ``) {
                    await handleConfigUpdate()
                    setCookie(AppCookies.RENEWAL_CAMPAIGN_CODE, window.myNtucConfig.renewalCampaignCode)
                }
                setLoading(true)

                try {
                    const renewalCode = window.myNtucConfig.renewalCampaignCode || cookies[AppCookies.RENEWAL_CAMPAIGN_CODE]

                    const response = await getMembershipRenewalFees(
                        window.myNtucConfig.membershipId,
                        renewalCode,
                    )

                    if (response.data.content.length > 0) {
                        setFees(response.data.content[0].fees)
                        setSelectedFee(response.data.content[0]?.fees[0])
                        setMembershipId(response.data.content[0].membershipId)
                        setIsArrear(response.data.content[0].arrearFlag)
                        setPeriodExpanded(
                            response.data.content[0].fees.length > 0
                                ? [true, ...Array(response.data.content[0].fees.length - 1).fill(false)]
                                : [],
                        )
                        setIsShowErrorSpecialCampaign(response.data.content[0].isBlocked)
                        setPeriodMessage(response.data.content[0].message)
                        if (response.data.content[0].message) {
                            setOpenPeriodAlert(true)
                        }
                    }
                } catch (error) {
                    console.error(`Error fetching membership renewal fees:`, error)
                } finally {
                    setLoading(false)
                }
            }
        }

        fetchData()
    }, [])

    const handleFeeSelection = (fee: FeeResponse, index: number) => {
        setSelectedFee(fee)
        setPeriodExpanded((prev) => {
            const updated = [...prev]
            updated.fill(false)
            updated[index] = true
            return updated
        })
    }

    const getDateRange = (fee: FeeResponse): string => {
        return `${moment(fee.startDate).format(`DD MMM YYYY`)} - ${moment(
            fee.endDate,
        ).format(`DD MMM YYYY`)}`
    }
    const getFeeTitle = (fee: FeeResponse): string => {
        return fee.feeType == FeeType.Renewal
            ? t(`payForNMonths`, { months: fee.month })
            : t(`payOutstandingFees`)
    }
    const GetFeeRows = () => {
        return (
            <>
                {` `}
                {fees.map((fee, index) => {
                    return (
                        <Grid key={index} item xs={12} marginBottom={2}>
                            <Accordion disableGutters={true} expanded={periodExpanded[index]}>
                                <AccordionSummary
                                    expandIcon={
                                        fee?.renewalGiftImage || fee?.renewalGiftDescription ? (
                                            <IconButton
                                                onClick={() => {
                                                    setPeriodExpanded((prev) => {
                                                        const clonedPrev = [...prev]
                                                        clonedPrev[index] = !clonedPrev[index]
                                                        return clonedPrev
                                                    })
                                                }}
                                            >
                                                <ArrowDropDownIcon />
                                            </IconButton>
                                        ) : (
                                            <></>
                                        )
                                    }
                                    aria-controls={`panel${index}-content`}
                                    id={`panel${index}-header`}
                                    sx={{ cursor: `unset !important` }}
                                >
                                    <Grid item xs={8}>
                                        <FormControlLabel
                                            value={fee}
                                            control={<Radio />}
                                            label={getFeeTitle(fee)}
                                            onClick={() => {
                                                handleFeeSelection(fee, index)
                                            }}
                                        />
                                        <Typography
                                            variant="body1"
                                            paddingLeft={4}
                                            color="text.secondary"
                                        >
                                            {getDateRange(fee)}
                                        </Typography>
                                    </Grid>
                                    <Grid item md={4} lg={4} xs={4} sm={4}>
                                        <Typography
                                            variant="body1"
                                            paddingRight={4}
                                            paddingTop={2}
                                            textAlign="right"
                                        >
                      ${fee.amount.toFixed(DECIMALS)}
                                        </Typography>
                                    </Grid>
                                </AccordionSummary>
                                {(fee?.renewalGiftImage || fee?.renewalGiftDescription) && (
                                    <AccordionDetails>
                                        <Grid item xs={12} marginTop={1}>
                                            <ImageList
                                                cols={1}
                                                sx={{ maxWidth: `80%`, margin: `auto` }}
                                            >
                                                <ImageListItem>
                                                    {fee.renewalGiftImage && (
                                                        <img
                                                            style={{ width: `80%`, margin: `auto` }}
                                                            src={`${window.myNtucConfig.assetsBaseUrl}${fee.renewalGiftImage}`}
                                                            alt={fee.renewalGiftDescription}
                                                        />
                                                    )}
                                                    {fee.renewalGiftDescription && (
                                                        <ImageListItemBar
                                                            title={`Description: ${fee.renewalGiftDescription}`}
                                                            position="below"
                                                            sx={{ textAlign: `center` }}
                                                        />
                                                    )}
                                                </ImageListItem>
                                            </ImageList>
                                        </Grid>
                                    </AccordionDetails>
                                )}
                            </Accordion>
                        </Grid>
                    )
                })}
            </>
        )
    }
    const ShowAllFees = () => {
        return (
            <Grid item xs={12} justifyContent="center">
                <RadioGroup
                    row
                    aria-labelledby="arrear-radio-buttons-group-label"
                    name="radio-buttons-group"
                    value={selectedFee}
                >
                    <GetFeeRows />
                </RadioGroup>
            </Grid>
        )
    }

    return (
        <>
            <Grid
                container
                justifyContent="center"
                padding={6}
                rowSpacing={2}
                columnSpacing={{
                    xs: `16px`,
                    sm: `24px`,
                    md: `24px`,
                    lg: `40px`,
                }}
            >
                <Grid item xs={12}>
                    <Typography variant="h2" align="center" paddingBottom={5}>
                        {t(`payMembershipFees`)}
                    </Typography>
                </Grid>
                <Grid item md={12} lg={12}>
                    <Typography variant="body1">{t(`selectPaymentPeriod`)}</Typography>
                </Grid>
                <Grid item xs={12} md={6} />
                <Grid item xs={12}>
                    <Typography mb={1} variant="h3">
                        {t(`title`)}
                    </Typography>
                    <Divider />
                </Grid>
                {!isShowErrorSpecialCampaign ?
                    <>
                        <ShowAllFees />
                        <Grid
                            container
                            justifyContent="space-evenly"
                            alignItems="center"
                            padding={5}
                            paddingLeft={10}
                        >
                            <LoadingButton
                                variant="contained"
                                color="primary"
                                size="large"
                                loading={loading}
                                disabled={selectedFee == undefined}
                                onClick={handleNextButton}
                            >
                                {t(`next`, { ns: `common` })}
                            </LoadingButton>
                        </Grid>
                    </> :
                    <WarningPanel
                        sx={{ mt: 3 }} // top spacing to match your layout
                        title={t(`membership_error_special_campaign`)} // "This campaign code cannot be used for membership renewal at the moment."
                    />
                }
            </Grid>
            <Snackbar
                open={openPeriodAlert}
                autoHideDuration={6000}
                onClose={handleClosePeriodAlert}
            >
                <Alert
                    onClose={handleClosePeriodAlert}
                    severity="error"
                    variant="filled"
                    sx={{ width: `100%` }}
                >
                    {periodMessage}
                </Alert>
            </Snackbar>
        </>
    )
}

export default MembershipFeePayment
